import { Injectable, inject } from '@angular/core'
import { TranslationService } from './translation.service'

@Injectable({
  providedIn: 'root',
})
export class FprCategoryService {
  private translationService = inject(TranslationService)

  private readonly CATEGORIES: Record<string, string>
  private readonly SUBCATEGORIES: Record<string, Record<string, string>>
  private readonly PACKAGING_CATEGORIES: Record<string, string>
  private readonly PACKAGING_SUBCATEGORIES: Record<
    string,
    Record<string, string>
  >

  constructor() {
    // Standard categories / subcategories
    this.CATEGORIES = {
      'Electronic and Electrical Equipment (EEE)':
        this.translationService.getCategoryElectronicAndElectricalEquipmentEeeText(),
      Tires: this.translationService.getCategoryTiresText(),
      Transportation: this.translationService.getCategoryTransportationText(),
      Construction: this.translationService.getCategoryConstructionText(),
      'Agriculture and horticulture':
        this.translationService.getCategoryAgricultureAndHorticultureText(),
      'Fishing and aquaculture':
        this.translationService.getCategoryFishingAndAquacultureText(),
      'Apparel and textiles':
        this.translationService.getCategoryApparelAndTextilesText(),
      'Single-use or disposable products':
        this.translationService.getCategorySingleUseOrDisposableProductsText(),
    }

    this.SUBCATEGORIES = {
      'Electronic and Electrical Equipment (EEE)': {
        'Electronic or electrical information technology or telecommunication devices or equipment':
          this.translationService.getSubcategoryElectronicAndElectricalEquipmentEeeElectronicOrElectricalInformationTechnologyOrTelecommunicationDevicesOrEquipmentText(),
        'Electronic or electrical audiovisual and consumer equipment or media':
          this.translationService.getSubcategoryElectronicAndElectricalEquipmentEeeElectronicOrElectricalAudiovisualAndConsumerEquipmentOrMediaText(),
        'Electronic or electrical appliances':
          this.translationService.getSubcategoryElectronicAndElectricalEquipmentEeeElectronicOrElectricalAppliancesText(),
        'Electronic or electrical tools, other than large-scale stationary industrial tools':
          this.translationService.getSubcategoryElectronicAndElectricalEquipmentEeeElectronicOrElectricalToolsOtherThanLargeScaleStationaryIndustrialToolsText(),
        'Electronic or electrical lighting equipment':
          this.translationService.getSubcategoryElectronicAndElectricalEquipmentEeeElectronicOrElectricalLightingEquipmentText(),
        'Electronic or electrical toys':
          this.translationService.getSubcategoryElectronicAndElectricalEquipmentEeeElectronicOrElectricalToysText(),
        'Electronic or electrical sports equipment':
          this.translationService.getSubcategoryElectronicAndElectricalEquipmentEeeElectronicOrElectricalSportsEquipmentText(),
        'Electronic or electrical devices for arts, hobbies or crafts':
          this.translationService.getSubcategoryElectronicAndElectricalEquipmentEeeElectronicOrElectricalDevicesForArtsHobbiesOrCraftsText(),
        'Electronic or electrical monitoring and control instruments':
          this.translationService.getSubcategoryElectronicAndElectricalEquipmentEeeElectronicOrElectricalMonitoringAndControlInstrumentsText(),
        'Electronic or electrical dispensers':
          this.translationService.getSubcategoryElectronicAndElectricalEquipmentEeeElectronicOrElectricalDispensersText(),
        'Electronic or electrical medical devices or equipment':
          this.translationService.getSubcategoryElectronicAndElectricalEquipmentEeeElectronicOrElectricalMedicalDevicesOrEquipmentText(),
        'Accessories for use with any products referred to in Category 1':
          this.translationService.getSubcategoryElectronicAndElectricalEquipmentEeeAccessoriesForUseWithAnyProductsReferredToInCategory1Text(),
        'Photovoltaic panels':
          this.translationService.getSubcategoryElectronicAndElectricalEquipmentEeePhotovoltaicPanelsText(),
        'Chargers for battery-electric vehicles and plug-in hybrid electric vehicles, whether free-standing or wall-mounted':
          this.translationService.getSubcategoryElectronicAndElectricalEquipmentEeeChargersForBatteryElectricVehiclesAndPlugInHybridElectricVehiclesWhetherFreeStandingOrWallMountedText(),
      },
      Tires: {
        'Tires for light-duty vehicles':
          this.translationService.getSubcategoryTiresTiresForLightDutyVehiclesText(),
        'Tires for medium-duty vehicles':
          this.translationService.getSubcategoryTiresTiresForMediumDutyVehiclesText(),
        'Tires for heavy-duty vehicles':
          this.translationService.getSubcategoryTiresTiresForHeavyDutyVehiclesText(),
        'Tires for motorcycles':
          this.translationService.getSubcategoryTiresTiresForMotorcyclesText(),
        'Tires for trailers':
          this.translationService.getSubcategoryTiresTiresForTrailersText(),
        'Tires for snowmobiles and restricted-use vehicles':
          this.translationService.getSubcategoryTiresTiresForSnowmobilesAndRestrictedUseVehiclesText(),
        'Tires for mobility aids':
          this.translationService.getSubcategoryTiresTiresForMobilityAidsText(),
        'Tires for bicycles, bicycle trailers and motor-assisted bicycles':
          this.translationService.getSubcategoryTiresTiresForBicyclesBicycleTrailersAndMotorAssistedBicyclesText(),
        'Tires for muscular-powered equipment':
          this.translationService.getSubcategoryTiresTiresForMuscularPoweredEquipmentText(),
      },
      Transportation: {
        'Light-duty vehicles':
          this.translationService.getSubcategoryTransportationLightDutyVehiclesText(),
        'Medium-duty vehicles':
          this.translationService.getSubcategoryTransportationMediumDutyVehiclesText(),
        'Heavy-duty vehicles':
          this.translationService.getSubcategoryTransportationHeavyDutyVehiclesText(),
        Motorcycles:
          this.translationService.getSubcategoryTransportationMotorcyclesText(),
        Trailers:
          this.translationService.getSubcategoryTransportationTrailersText(),
        'Snowmobiles and restricted-use vehicles':
          this.translationService.getSubcategoryTransportationSnowmobilesAndRestrictedUseVehiclesText(),
        'Mobility aids':
          this.translationService.getSubcategoryTransportationMobilityAidsText(),
        'Bicycles, bicycle trailers and motor-assisted bicycles':
          this.translationService.getSubcategoryTransportationBicyclesBicycleTrailersAndMotorAssistedBicyclesText(),
        'Muscular-powered equipment':
          this.translationService.getSubcategoryTransportationMuscularPoweredEquipmentText(),
      },
      Construction: {
        'Windows and doors':
          this.translationService.getSubcategoryConstructionWindowsAndDoorsText(),
        'Interior finishes (including flooring)':
          this.translationService.getSubcategoryConstructionInteriorFinishesIncludingFlooringText(),
        'Construction film and sheeting':
          this.translationService.getSubcategoryConstructionConstructionFilmAndSheetingText(),
        'Insulation materials':
          this.translationService.getSubcategoryConstructionInsulationMaterialsText(),
        'Paints and coatings':
          this.translationService.getSubcategoryConstructionPaintsAndCoatingsText(),
        'Piping, including pipe fittings':
          this.translationService.getSubcategoryConstructionPipingIncludingPipeFittingsText(),
        'Roofing materials':
          this.translationService.getSubcategoryConstructionRoofingMaterialsText(),
        'Siding and cladding':
          this.translationService.getSubcategoryConstructionSidingAndCladdingText(),
        'Decking and fencing':
          this.translationService.getSubcategoryConstructionDeckingAndFencingText(),
      },
      'Agriculture and horticulture': {
        'Agricultural containers':
          this.translationService.getSubcategoryAgricultureAndHorticultureAgriculturalContainersText(),
        'Animal health product containers':
          this.translationService.getSubcategoryAgricultureAndHorticultureAnimalHealthProductContainersText(),
        'Agricultural totes and drums':
          this.translationService.getSubcategoryAgricultureAndHorticultureAgriculturalTotesAndDrumsText(),
        'Agricultural bags and large tote bags':
          this.translationService.getSubcategoryAgricultureAndHorticultureAgriculturalBagsAndLargeToteBagsText(),
        'Clips, supports, hooks':
          this.translationService.getSubcategoryAgricultureAndHorticultureClipsSupportsHooksText(),
        'Bale wrap, bags or tubes':
          this.translationService.getSubcategoryAgricultureAndHorticultureBaleWrapBagsOrTubesText(),
        'Grain bags':
          this.translationService.getSubcategoryAgricultureAndHorticultureGrainBagsText(),
        'Agricultural and horticultural film and sheeting (e.g. for silage, mulch, flooring, plant protection)':
          this.translationService.getSubcategoryAgricultureAndHorticultureAgriculturalAndHorticulturalFilmAndSheetingEGForSilageMulchFlooringPlantProtectionText(),
        'Net wrap':
          this.translationService.getSubcategoryAgricultureAndHorticultureNetWrapText(),
        'Structural plastic for greenhouses':
          this.translationService.getSubcategoryAgricultureAndHorticultureStructuralPlasticForGreenhousesText(),
        'CO2 tubing for greenhouses':
          this.translationService.getSubcategoryAgricultureAndHorticultureCo2TubingForGreenhousesText(),
        'Propagation trays and growing pots':
          this.translationService.getSubcategoryAgricultureAndHorticulturePropagationTraysAndGrowingPotsText(),
        'Irrigation equipment':
          this.translationService.getSubcategoryAgricultureAndHorticultureIrrigationEquipmentText(),
        'Maple syrup tubing and taps':
          this.translationService.getSubcategoryAgricultureAndHorticultureMapleSyrupTubingAndTapsText(),
        'Agricultural and horticultural twine':
          this.translationService.getSubcategoryAgricultureAndHorticultureAgriculturalAndHorticulturalTwineText(),
      },
      'Fishing and aquaculture': {
        Longlines:
          this.translationService.getSubcategoryFishingAndAquacultureLonglinesText(),
        'Nets (e.g. seines, gillnet mesh and associated float and sink lines)':
          this.translationService.getSubcategoryFishingAndAquacultureNetsEGSeinesGillnetMeshAndAssociatedFloatAndSinkLinesText(),
        'Traps and pots':
          this.translationService.getSubcategoryFishingAndAquacultureTrapsAndPotsText(),
        'Trawl nets':
          this.translationService.getSubcategoryFishingAndAquacultureTrawlNetsText(),
        Trolls:
          this.translationService.getSubcategoryFishingAndAquacultureTrollsText(),
        'Buoy lines':
          this.translationService.getSubcategoryFishingAndAquacultureBuoyLinesText(),
        Anchors:
          this.translationService.getSubcategoryFishingAndAquacultureAnchorsText(),
        'Fishing gear markers':
          this.translationService.getSubcategoryFishingAndAquacultureFishingGearMarkersText(),
        Buoys:
          this.translationService.getSubcategoryFishingAndAquacultureBuoysText(),
        Weirs:
          this.translationService.getSubcategoryFishingAndAquacultureWeirsText(),
        'Groundlines (e.g. ropes and bridles connecting traps/pots in a trawl)':
          this.translationService.getSubcategoryFishingAndAquacultureGroundlinesEGRopesAndBridlesConnectingTrapsPotsInATrawlText(),
        'Bait bags':
          this.translationService.getSubcategoryFishingAndAquacultureBaitBagsText(),
        'Weak links/whale release systems in buoy lines':
          this.translationService.getSubcategoryFishingAndAquacultureWeakLinksWhaleReleaseSystemsInBuoyLinesText(),
        'Smart Buoys, smart release buoy systems, other emerging technology for fishing gear recovery systems':
          this.translationService.getSubcategoryFishingAndAquacultureSmartBuoysSmartReleaseBuoySystemsOtherEmergingTechnologyForFishingGearRecoverySystemsText(),
        'On-Demand/Ropeless Fishing Systems (floats, inflatable bags, acoustic releases, rope containment systems)':
          this.translationService.getSubcategoryFishingAndAquacultureOnDemandRopelessFishingSystemsFloatsInflatableBagsAcousticReleasesRopeContainmentSystemsText(),
        'Buckets, bins and barrels':
          this.translationService.getSubcategoryFishingAndAquacultureBucketsBinsAndBarrelsText(),
        'Flotation for raft structures for aquaculture':
          this.translationService.getSubcategoryFishingAndAquacultureFlotationForRaftStructuresForAquacultureText(),
        'Ropes, bags and netting for aquaculture':
          this.translationService.getSubcategoryFishingAndAquacultureRopesBagsAndNettingForAquacultureText(),
        'Trays for aquaculture':
          this.translationService.getSubcategoryFishingAndAquacultureTraysForAquacultureText(),
      },
      'Apparel and textiles': {
        Apparel:
          this.translationService.getSubcategoryApparelAndTextilesApparelText(),
        'Interior textiles':
          this.translationService.getSubcategoryApparelAndTextilesInteriorTextilesText(),
        'Industrial textiles':
          this.translationService.getSubcategoryApparelAndTextilesIndustrialTextilesText(),
        'Carpet and other floor coverings':
          this.translationService.getSubcategoryApparelAndTextilesCarpetAndOtherFloorCoveringsText(),
        Footwear:
          this.translationService.getSubcategoryApparelAndTextilesFootwearText(),
        'Yarn, thread, twine, cordage, ropes, cables, woven and knitted or crocheted fabric':
          this.translationService.getSubcategoryApparelAndTextilesYarnThreadTwineCordageRopesCablesWovenAndKnittedOrCrochetedFabricText(),
        Luggage:
          this.translationService.getSubcategoryApparelAndTextilesLuggageText(),
        'Reusable fabric checkout bags':
          this.translationService.getSubcategoryApparelAndTextilesReusableFabricCheckoutBagsText(),
      },
      'Single-use or disposable products': {
        'Food service ware - Clamshell containers, lidded containers, boxes, plates, and bowls':
          this.translationService.getSubcategorySingleUseOrDisposableProductsFoodServiceWareClamshellContainersLiddedContainersBoxesPlatesAndBowlsText(),
        'Food service ware - Takeaway cups':
          this.translationService.getSubcategorySingleUseOrDisposableProductsFoodServiceWareTakeawayCupsText(),
        'Food service ware - Lids for takeaway cups':
          this.translationService.getSubcategorySingleUseOrDisposableProductsFoodServiceWareLidsForTakeawayCupsText(),
        'Food service ware - Rigid ring carriers':
          this.translationService.getSubcategorySingleUseOrDisposableProductsFoodServiceWareRigidRingCarriersText(),
        'Food service ware - Foam trays':
          this.translationService.getSubcategorySingleUseOrDisposableProductsFoodServiceWareFoamTraysText(),
        'Food service ware - Single-serve capsules and pods':
          this.translationService.getSubcategorySingleUseOrDisposableProductsFoodServiceWareSingleServeCapsulesAndPodsText(),
        'Food service ware - Bags provided by a retailer and filled within the store with produce or bulk products':
          this.translationService.getSubcategorySingleUseOrDisposableProductsFoodServiceWareBagsProvidedByARetailerAndFilledWithinTheStoreWithProduceOrBulkProductsText(),
        'Personal hygiene and care products - Manual toothbrushes and replaceable toothbrush heads':
          this.translationService.getSubcategorySingleUseOrDisposableProductsPersonalHygieneAndCareProductsManualToothbrushesAndReplaceableToothbrushHeadsText(),
        'Personal hygiene and care products - Disposable diapers and menstrual products':
          this.translationService.getSubcategorySingleUseOrDisposableProductsPersonalHygieneAndCareProductsDisposableDiapersAndMenstrualProductsText(),
        'Personal hygiene and care products - Tubes for personal hygiene and care products':
          this.translationService.getSubcategorySingleUseOrDisposableProductsPersonalHygieneAndCareProductsTubesForPersonalHygieneAndCareProductsText(),
        'Personal hygiene and care products - Razors and razor heads':
          this.translationService.getSubcategorySingleUseOrDisposableProductsPersonalHygieneAndCareProductsRazorsAndRazorHeadsText(),
        'Personal hygiene and care products - Dental floss and flossers':
          this.translationService.getSubcategorySingleUseOrDisposableProductsPersonalHygieneAndCareProductsDentalFlossAndFlossersText(),
        'Personal hygiene and care products - Cotton swab sticks':
          this.translationService.getSubcategorySingleUseOrDisposableProductsPersonalHygieneAndCareProductsCottonSwabSticksText(),
        'Personal hygiene and care products - Wipes':
          this.translationService.getSubcategorySingleUseOrDisposableProductsPersonalHygieneAndCareProductsWipesText(),
        'Novelty items':
          this.translationService.getSubcategorySingleUseOrDisposableProductsNoveltyItemsText(),
        'Tobacco and vaping products - Tobacco products containing filters':
          this.translationService.getSubcategorySingleUseOrDisposableProductsTobaccoAndVapingProductsTobaccoProductsContainingFiltersText(),
        'Tobacco and vaping products - Vaping devices':
          this.translationService.getSubcategorySingleUseOrDisposableProductsTobaccoAndVapingProductsVapingDevicesText(),
        'Tobacco and vaping products - Vaping cartridges':
          this.translationService.getSubcategorySingleUseOrDisposableProductsTobaccoAndVapingProductsVapingCartridgesText(),
        'Personal protective equipment - Masks':
          this.translationService.getSubcategorySingleUseOrDisposableProductsPersonalProtectiveEquipmentMasksText(),
        'Personal protective equipment - Gloves':
          this.translationService.getSubcategorySingleUseOrDisposableProductsPersonalProtectiveEquipmentGlovesText(),
      },
    }

    // Packaging categories / subcategories
    this.PACKAGING_CATEGORIES = {
      'Plastic Packaging Filled':
        this.translationService.getPackagingCategoryPlasticFilledText(),
      'Plastic Packaging Unfilled':
        this.translationService.getPackagingCategoryPlasticUnfilledText(),
    }

    const rigid = this.translationService.getRigidText()
    const flexible = this.translationService.getFlexibleText()

    const beverage =
      this.translationService.getPackagingSubcategoryBeverageContainerText()
    const foodContact =
      this.translationService.getPackagingSubcategoryFoodContactMaterialText()
    const hazardous =
      this.translationService.getPackagingSubcategoryHazardousMaterialsPackagingText()
    const other =
      this.translationService.getPackagingSubcategoryOtherPackagingText()

    const packagingSubcategories: Record<string, string> = {
      'Rigid - Beverage container': `${rigid} - ${beverage}`,
      'Rigid - Food contact material': `${rigid} - ${foodContact}`,
      'Rigid - Packaging for hazardous materials': `${rigid} - ${hazardous}`,
      'Rigid - Other packaging': `${rigid} - ${other}`,
      'Flexible - Beverage container': `${flexible} - ${beverage}`,
      'Flexible - Food contact material': `${flexible} - ${foodContact}`,
      'Flexible - Packaging for hazardous materials': `${flexible} - ${hazardous}`,
      'Flexible - Other packaging': `${flexible} - ${other}`,
    }

    this.PACKAGING_SUBCATEGORIES = {
      'Plastic Packaging Filled': { ...packagingSubcategories },
      'Plastic Packaging Unfilled': { ...packagingSubcategories },
    }
  }

  getCategories(isPackaging: boolean): Record<string, string> {
    return isPackaging ? this.PACKAGING_CATEGORIES : this.CATEGORIES
  }

  getSubcategories(
    isPackaging: boolean,
  ): Record<string, Record<string, string>> {
    return isPackaging ? this.PACKAGING_SUBCATEGORIES : this.SUBCATEGORIES
  }

  getSubcategoriesForCategory(
    category: string,
    isPackaging: boolean,
  ): Record<string, string> {
    const map = this.getSubcategories(isPackaging)
    return map[category] ?? {}
  }

  getSubcategorySelection(
    category: string,
    currentSubcategory: string,
    isPackaging: boolean,
  ): {
    subcategoriesForSelectedCategory: Record<string, string>
    sanitizedSubcategory: string
  } {
    const subcategoriesForSelectedCategory = this.getSubcategoriesForCategory(
      category,
      isPackaging,
    )

    const sanitizedSubcategory =
      currentSubcategory &&
      !subcategoriesForSelectedCategory[currentSubcategory]
        ? ''
        : currentSubcategory

    return { subcategoriesForSelectedCategory, sanitizedSubcategory }
  }
}
